/**
 * inventory.service.ts
 *
 * Typed API call wrappers for the Inventory domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Adjustment types a staff user can pick in the UI. */
export type ManualAdjustmentType =
  | 'add'
  | 'remove'
  | 'sale'
  | 'return'
  | 'shrinkage'
  | 'adjustment';

/**
 * Full set of adjustment types recorded in inventory_transactions.
 * `purchase_receive` is system-only and must not appear in manual pickers.
 */
export type AdjustmentType = ManualAdjustmentType | 'purchase_receive';

/**
 * Server-computed stock status. The one ladder for the module — every surface
 * reads this rather than re-deriving its own quantity comparison.
 * `out` = at or below zero, `low` = above zero but at or below the reorder
 * threshold, `ok` = everything else (including products with no threshold set).
 */
export type StockStatus = 'out' | 'low' | 'ok';

export interface Product {
  productId: number;
  accountId: number;
  name: string;
  description: string | null;
  sku: string | null;
  unit: string | null;
  category: string | null;
  /** Home facility (`quote_item_costs.facilityID`). */
  facilityId: number | null;
  /**
   * Always null. A product has one stock count (`stockQuantity`) that no
   * facility filter changes; the key stays in the response shape.
   */
  facilityOnHand: number | null;
  unitPrice: number;
  cost: number | null;
  stockQuantity: number;
  lowStockAlert: number;
  /** Server-computed status from `stockQuantity`, so it always agrees with the count shown. */
  stockStatus: StockStatus;
  minOrderQuantity: number | null;
  vendorId: number | null;
  active: boolean;
  taxable: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  /** Catalog (read-only): product type linked via quote_item_costs_types. */
  productTypeId?: number | null;
  productTypeName?: string | null;
  /** Merchandising category IDs (product_type_categories by productTypeID). */
  productCategoryIds?: number[];
  /** Default image for the product's type (product_image_urls, defaultUrl=1). */
  imageUrl?: string | null;
}

/** Per-facility stock row for a single product. */
export interface ProductFacilityStock {
  facilityId: number;
  name: string | null;
  isHome: boolean;
  onHand: number;
}

export interface InventoryAdjustment {
  adjustmentId: number;
  productId: number;
  productName: string;
  accountId: number;
  adjustmentType: AdjustmentType;
  quantity: number;
  previousQty: number;
  newQty: number;
  notes: string | null;
  userId: number | null;
  patientId: number | null;
  patientName: string | null;
  appointmentId: number | null;
  purchaseOrderId: number | null;
  batchNumber: string | null;
  createdAt: string;
}

export interface CreateProductPayload {
  name: string;
  description?: string;
  sku?: string;
  unit?: string;
  category?: string;
  facilityId?: number;
  unitPrice: number;
  cost?: number;
  stockQuantity?: number;
  lowStockAlert?: number;
  minOrderQuantity?: number;
  vendorId?: number;
  taxable?: boolean;
}

export interface UpdateProductPayload extends Partial<CreateProductPayload> {
  active?: boolean;
}

export interface AdjustStockPayload {
  adjustmentType: AdjustmentType;
  quantity: number;
  notes?: string;
  patientId?: number;
  appointmentId?: number;
  purchaseOrderId?: number;
  batchNumber?: string;
}

export interface ProductListParams {
  search?: string;
  category?: string;
  categories?: string[];
  categoriesNegate?: boolean;
  lowStock?: boolean;
  /** Multi-value stock-status checklist (`'out' | 'low' | 'ok'`). */
  stockLevels?: StockStatus[];
  stockLevelsNegate?: boolean;
  active?: boolean;
  /** Multi-value status checklist (`'active' | 'inactive'`). */
  statuses?: string[];
  statusesNegate?: boolean;
  vendorId?: number;
  page?: number;
  perPage?: number;
  /** A Stock list column key; the API ignores keys it does not whitelist. */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  export?: boolean;
  /** Restrict the list to products based at, or carried at, a facility. */
  facilityId?: number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  /** Restrict list to products linked to the given `product_types.productTypeID`. */
  productTypeId?: number;
  productTypeIds?: number[];
  productTypeIdsNegate?: boolean;
}

export interface PaginatedProducts {
  data: Product[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
    /** Echoes the active `facilityId` filter when present. */
    facilityId?: number | null;
    /** Echoes the active `productTypeId` filter when present. */
    productTypeId?: number | null;
  };
}

export interface ProductServiceUsage {
  usageId: number;
  accountId: number;
  productId: number;
  procedureId: number;
  procedureName?: string;
  productName?: string;
  expectedQuantity: number;
  notes: string | null;
  active: boolean;
  createdAt: string;
}

/**
 * One row per product category.
 *
 * There is no cost-basis column on `quote_item_costs` — `curRate` is the sale
 * rate — so cost value and margin cannot be reported without a schema change.
 *
 * `totalUnits` and `totalRetailValue` count stock above zero only. On-hand goes
 * negative by design, and those products are reported in `belowZeroCount` and
 * `belowZeroUnits` (a sum of the negative balances) instead of being netted
 * against the rest.
 */
export interface InventoryValuation {
  category: string | null;
  productCount: number;
  totalUnits: number;
  totalRetailValue: number;
  belowZeroCount: number;
  belowZeroUnits: number;
}

export interface UsageTrendEntry {
  date: string;
  totalAdjustments: number;
  totalAdded: number;
  totalRemoved: number;
}

export interface ShrinkageEntry {
  productId: number;
  productName: string;
  totalShrinkage: number;
  adjustmentCount: number;
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

export async function listProducts(params?: ProductListParams): Promise<PaginatedProducts> {
  const res = await apiClient.get<PaginatedProducts>('/inventory', { params });
  return res.data;
}

export async function getProduct(productId: number): Promise<Product> {
  const res = await apiClient.get<{ data: Product }>(`/inventory/${productId}`);
  return res.data.data;
}

export async function createProduct(payload: CreateProductPayload): Promise<Product> {
  const res = await apiClient.post<{ data: Product }>('/inventory', payload);
  return res.data.data;
}

export async function updateProduct(
  productId: number,
  payload: UpdateProductPayload,
): Promise<Product> {
  const res = await apiClient.put<{ data: Product }>(`/inventory/${productId}`, payload);
  return res.data.data;
}

export async function deleteProduct(productId: number): Promise<void> {
  await apiClient.delete(`/inventory/${productId}`);
}

export interface AdjustStockResult {
  previousQty: number;
  newQty: number;
}

export async function adjustStock(
  productId: number,
  payload: AdjustStockPayload,
): Promise<AdjustStockResult> {
  const res = await apiClient.post<{ data: AdjustStockResult }>(
    `/inventory/${productId}/adjust`,
    payload,
  );
  return res.data.data;
}

export async function getAdjustmentHistory(
  productId: number,
  limit = 20,
): Promise<InventoryAdjustment[]> {
  const res = await apiClient.get<{ data: InventoryAdjustment[] }>(
    `/inventory/${productId}/history`,
    { params: { limit } },
  );
  return res.data.data;
}

/** Per-facility stock breakdown for a single product. */
export async function listProductFacilities(
  productId: number,
): Promise<ProductFacilityStock[]> {
  const res = await apiClient.get<{ data: ProductFacilityStock[] }>(
    `/inventory/${productId}/facilities`,
  );
  return res.data.data;
}

export async function listCategories(): Promise<string[]> {
  const res = await apiClient.get<{ data: unknown }>('/inventory/categories');
  const rows = Array.isArray(res.data.data) ? res.data.data : [];
  // Older API builds returned full quote_item_costs_category rows instead of
  // titles; objects fed into filter options crash the checklist render, so
  // normalize either shape down to a clean string[].
  return rows
    .map((row) => {
      if (typeof row === 'string') return row.trim();
      if (row && typeof row === 'object' && 'title' in row) {
        const title = (row as { title?: unknown }).title;
        return typeof title === 'string' ? title.trim() : '';
      }
      return '';
    })
    .filter((title) => title.length > 0);
}

// ─── Catalog lookups (read-only) ──────────────────────────────────────────────
// Phase 1.5: surface legacy catalog data (product types + merchandising
// categories) on the inventory screens. These wrap existing v5 endpoints.

export interface ProductTypeLookupEntry {
  productTypeID: number;
  productTypeName: string | null;
  productTypePublicName?: string | null;
  activeProductsCount?: number;
  /** 1 = live, 0 = deleted. GET /product-types lists both. */
  status?: number;
  /** 0 = Product, 1 = Credit. */
  productCredits?: number | string;
  /** 0 = Appointment disbursal, 1 = Payment (retail) disbursal. */
  disburseType?: number;
  description?: string | null;
}

export interface ProductCategoryLookupEntry {
  productCategoryID: number;
  productCategoryName: string | null;
  productCategoryPublicName?: string | null;
}

export async function listProductTypes(): Promise<ProductTypeLookupEntry[]> {
  const res = await apiClient.get<{ data: ProductTypeLookupEntry[] }>('/product-types');
  return res.data.data;
}

export async function listProductCategoriesLookup(): Promise<ProductCategoryLookupEntry[]> {
  const res = await apiClient.get<{ data: ProductCategoryLookupEntry[] }>(
    '/product-categories-lookup',
  );
  return res.data.data;
}

// ─── Stock alerts ─────────────────────────────────────────────────────────────

/**
 * Products needing attention — out of stock, or at/below their reorder
 * threshold. Returned most-depleted first.
 *
 * Note the route name is historical; it covers out-of-stock too. Always call
 * this with explicit arguments — passing the bare function reference to a React
 * Query `queryFn` forwards the query context object as axios params.
 */
export function listLowStockProducts(): Promise<Product[]>;
export function listLowStockProducts(params: Omit<ProductListParams, 'page' | 'perPage' | 'lowStock'>): Promise<Product[]>;
export async function listLowStockProducts(params: Omit<ProductListParams, 'page' | 'perPage' | 'lowStock'> = {}): Promise<Product[]> {
  const res = await apiClient.get<{ data: Product[] }>('/inventory/low-stock', { params });
  return res.data.data;
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export async function getValuationReport(): Promise<InventoryValuation[]> {
  const res = await apiClient.get<{ data: InventoryValuation[] }>('/inventory/reports/valuation');
  return res.data.data;
}

export async function getUsageTrendsReport(params?: {
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month';
}): Promise<UsageTrendEntry[]> {
  const res = await apiClient.get<{ data: UsageTrendEntry[] }>('/inventory/reports/usage', { params });
  return res.data.data;
}

export async function getShrinkageReport(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ShrinkageEntry[]> {
  const res = await apiClient.get<{ data: ShrinkageEntry[] }>('/inventory/reports/shrinkage', { params });
  return res.data.data;
}

// ─── Service usage ────────────────────────────────────────────────────────────
// Reserved for a future phase (service-to-product consumption).
//
// The wrappers that used to live here were removed: they called
// `/inventory/service-usage` and `/inventory/{id}/service-usage`, and neither
// route exists in routes/api.php. They had no callers, so nothing 404'd — but
// the comment claiming the backend existed would have misled the next reader.
// Reintroduce them alongside the endpoints, not before.
